import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router";
import { useApp } from "../context/AppContext";
import {
  LayoutDashboard, Users, ListChecks, ClipboardEdit, LogOut,
  Menu, X, Clock, MessageSquare, TriangleAlert, ChevronRight,
  FileBarChart2, Bell, Globe, UserCheck, UserPlus, Archive
} from "lucide-react";
import BpsLogo from "../components/BpsLogo";

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, logout } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const adminLinks = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/pengunjung", label: "Data Pengunjung", icon: Users },
    { to: "/admin/petugas", label: "Manajemen Petugas", icon: UserCheck },
    { to: "/admin/pengaduan", label: "Pengaduan", icon: TriangleAlert },
    { to: "/admin/laporan", label: "Laporan & Export", icon: FileBarChart2 },
    { to: "/admin/whatsapp", label: "Monitor WA Halo Semeton", icon: MessageSquare },
    { to: "/admin/bank-data", label: "Bank Data", icon: Archive },
  ];

  const petugasLinks = [
    { to: "/petugas", label: "Dashboard", icon: LayoutDashboard },
    { to: "/petugas/presensi", label: "Presensi", icon: Clock },
    { to: "/petugas/layanan", label: "Manajemen Layanan", icon: ListChecks },
    { to: "/petugas/konsultasi", label: "Input Lapor.go.id", icon: ClipboardEdit },
    { to: "/petugas/whatsapp", label: "WhatsApp Halo Semeton", icon: MessageSquare },
    { to: "/petugas/daftar-tamu", label: "Daftar Tamu", icon: UserPlus },
    { to: "/petugas/bank-data", label: "Bank Data", icon: Archive },
  ];

  const links = currentUser?.role === "admin" ? adminLinks : petugasLinks;
  const basePath = currentUser?.role === "admin" ? "/admin" : "/petugas";

  const isActive = (path: string) =>
    path === basePath ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-[#003580] text-white z-30 flex flex-col
        transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        md:translate-x-0 md:relative md:z-auto
      `}>
        {/* Logo */}
        <div className="p-4 border-b border-white/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BpsLogo size={40} />
            <div>
              <div className="text-sm" style={{fontWeight:700}}>PST BPS</div>
              <div className="text-xs opacity-75">Lombok Tengah</div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1 rounded hover:bg-white/10">
            <X size={18} />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-white/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-sm" style={{fontWeight:700}}>
              {currentUser?.avatar}
            </div>
            <div className="min-w-0">
              <div className="text-sm truncate" style={{fontWeight:600}}>{currentUser?.name}</div>
              <div className="text-xs opacity-75 truncate">{currentUser?.jabatan}</div>
              <span className={`inline-block mt-0.5 text-xs px-2 py-0.5 rounded-full ${
                currentUser?.role === "admin" ? "bg-yellow-400 text-yellow-900" : "bg-green-400 text-green-900"
              }`} style={{fontWeight:600}}>
                {currentUser?.role === "admin" ? "Admin" : "Petugas"}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {links.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive(link.to)
                  ? "bg-white text-[#003580] shadow"
                  : "text-white/90 hover:bg-white/10"
              }`}
              style={{fontWeight: isActive(link.to) ? 600 : 400}}
            >
              <link.icon size={18} />
              <span>{link.label}</span>
              {isActive(link.to) && <ChevronRight size={14} className="ml-auto" />}
            </Link>
          ))}
        </nav>

        {/* Bottom links */}
        <div className="p-3 border-t border-white/20 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 transition-colors">
            <Globe size={16} />
            <span>Halaman Publik</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-300 hover:bg-red-500/20 transition-colors"
          >
            <LogOut size={16} />
            <span>Keluar</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="bg-white shadow-sm px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-base" style={{fontWeight:600, color:"#003580"}}>
                {currentUser?.role === "admin" ? "Admin Panel" : "Petugas Panel"}
              </h1>
              <p className="text-xs text-gray-500">
                {new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 relative">
              <Bell size={18} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="w-8 h-8 rounded-full bg-[#003580] text-white flex items-center justify-center text-xs" style={{fontWeight:700}}>
              {currentUser?.avatar}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
